import type { FetchError } from 'src/types/fetch';
import type { GenericSelectorProductType } from 'src/types/product';
import { getSdk } from 'src/sdk';

export type FetchProductModelParams = {
    productId: string;
    version?: string | number;
};

/**
 * Fetches a product's full definition (options and property rules/assignments) from the Cimpress Product Service.
 *
 * Goes through the SDK's `Products.getByVersion` (or `getCurrentMCP` if no version).
 *
 * Resolves to `null` on 404 or missing product.
 */
export async function fetchProductModel({
    productId,
    version,
}: FetchProductModelParams): Promise<GenericSelectorProductType | null> {
    if (!productId) {
        return null;
    }

    const hasVersion = version !== undefined && version !== '';

    try {
        const response = hasVersion
            ? await getSdk().Products.getByVersion(productId, Number(version))
            : await getSdk().Products.getCurrentMCP(productId);

        if (!response) {
            return null;
        }

        // the SDK types nullable fields where the app's types use optional ones; the payload is the same
        const model: GenericSelectorProductType = {};
        if ('options' in response && response.options) {
            model.options = response.options as GenericSelectorProductType['options'];
        }
        if ('properties' in response && response.properties) {
            model.properties = response.properties as GenericSelectorProductType['properties'];
        }
        return model;
    } catch (error) {
        if ((error as FetchError)?.response?.status === 404) {
            return null;
        }
        throw error;
    }
}
